import { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { useCart } from './CartContext';
import { useLocation } from 'react-router-dom';
import { useVoice } from './voice';

// Tutorial steps - each step waits for a specific condition
export type TutorialStep =
  | 'welcome'           // Initial overlay pointing to voice button
  | 'click_mic'         // Waiting for user to click the mic
  | 'greeting'          // Agent says hello, mic muted
  | 'unmute_prompt'     // Tell user to unmute and search for "shirt"
  | 'wait_search'       // Wait for search results to appear
  | 'buy_prompt'        // Tell user to say "I want to buy that t-shirt"
  | 'wait_cart_add'     // Wait for item to be added to cart
  | 'show_cart_prompt'  // Tell user to say "show my cart"
  | 'wait_cart_view'    // Wait for cart page
  | 'order_prompt'      // Tell user to say "I want to order"
  | 'wait_checkout'     // Wait for checkout page
  | 'complete';         // Final screen with capabilities

interface TutorialContextType {
  isActive: boolean;
  currentStep: TutorialStep;
  hasCompletedTutorial: boolean;
  startTutorial: () => void;
  quitTutorial: () => void;
  advanceStep: () => void;
  goToStep: (step: TutorialStep) => void;
  markComplete: () => void;
}

const TutorialContext = createContext<TutorialContextType | null>(null);

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
}

const TUTORIAL_STORAGE_KEY = 'wehkamp_tutorial_completed';

const STEP_ORDER: TutorialStep[] = [
  'welcome',
  'click_mic',
  'greeting',
  'unmute_prompt',
  'wait_search',
  'buy_prompt',
  'wait_cart_add',
  'show_cart_prompt',
  'wait_cart_view',
  'order_prompt',
  'wait_checkout',
  'complete',
];

interface TutorialProviderProps {
  children: ReactNode;
}

export function TutorialProvider({ children }: TutorialProviderProps) {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState<TutorialStep>('welcome');
  const [hasCompletedTutorial, setHasCompletedTutorial] = useState(() => {
    return localStorage.getItem(TUTORIAL_STORAGE_KEY) === 'true';
  });

  const { getCartCount } = useCart();
  const location = useLocation();
  const { state: voiceState } = useVoice();
  const cartCount = getCartCount();

  // Track initial cart count to detect additions (only set ONCE when entering buy_prompt)
  const initialCartCount = useRef<number | null>(null);
  useEffect(() => {
    console.log('[Tutorial] Cart effect - step:', currentStep, 'ref:', initialCartCount.current, 'cartCount:', cartCount);
    if (currentStep === 'buy_prompt' && initialCartCount.current === null) {
      initialCartCount.current = cartCount;
      console.log('[Tutorial] ✓ Set initial cart count:', initialCartCount.current);
    }
    // Reset when leaving buy_prompt related steps
    if (!['buy_prompt', 'wait_cart_add', 'show_cart_prompt'].includes(currentStep)) {
      if (initialCartCount.current !== null) {
        console.log('[Tutorial] Reset initial cart count');
        initialCartCount.current = null;
      }
    }
  }, [currentStep, cartCount]);

  // Track when we entered wait_search to ensure minimum display time
  const waitSearchEnteredAt = useRef<number>(0);
  const [waitSearchReady, setWaitSearchReady] = useState(false);

  // When entering wait_search, start a minimum display timer
  useEffect(() => {
    if (currentStep === 'wait_search') {
      waitSearchEnteredAt.current = Date.now();
      setWaitSearchReady(false);
      // Minimum 2 seconds to show "Zoeken..." message
      const timer = setTimeout(() => {
        setWaitSearchReady(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  // Auto-advance based on conditions
  useEffect(() => {
    if (!isActive) return;

    const searchParams = new URLSearchParams(location.search);
    const hasSearch = searchParams.has('search');

    // When user speaks during unmute_prompt, go to wait_search
    if (currentStep === 'unmute_prompt' && voiceState === 'speaking') {
      console.log('[Tutorial] User speaking, going to wait_search');
      setCurrentStep('wait_search');
      return;
    }

    // When search URL appears AND we've shown wait_search for minimum time, advance
    if (currentStep === 'wait_search' && hasSearch && waitSearchReady) {
      console.log('[Tutorial] Search found and ready, going to buy_prompt');
      setCurrentStep('buy_prompt');
      return;
    }

    // Wait for item added to cart (detect new items)
    if (currentStep === 'wait_cart_add' || currentStep === 'buy_prompt') {
      console.log('[Tutorial] Checking cart - step:', currentStep, 'initial:', initialCartCount.current, 'current:', cartCount);
      if (initialCartCount.current !== null && cartCount > initialCartCount.current) {
        console.log('[Tutorial] Cart item added! Advancing to show_cart_prompt');
        setCurrentStep('show_cart_prompt');
        return;
      }
    }

    // Wait for cart page
    if (currentStep === 'wait_cart_view' || currentStep === 'show_cart_prompt') {
      if (location.pathname === '/winkelwagen') {
        console.log('[Tutorial] On cart page, advancing to order_prompt');
        setCurrentStep('order_prompt');
        return;
      }
    }

    // Wait for checkout
    if (currentStep === 'wait_checkout' || currentStep === 'order_prompt') {
      if (location.pathname === '/afrekenen') {
        console.log('[Tutorial] On checkout page, advancing to complete');
        setCurrentStep('complete');
        return;
      }
    }
  }, [isActive, currentStep, cartCount, location.pathname, location.search, voiceState]);

  const startTutorial = useCallback(() => {
    setCurrentStep('welcome');
    setIsActive(true);
  }, []);

  const quitTutorial = useCallback(() => {
    // Mark as completed so it doesn't auto-start again
    localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
    setHasCompletedTutorial(true);
    setIsActive(false);
    setCurrentStep('welcome');
  }, []);

  const advanceStep = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    if (currentIndex < STEP_ORDER.length - 1) {
      setCurrentStep(STEP_ORDER[currentIndex + 1]);
    }
  }, [currentStep]);

  const goToStep = useCallback((step: TutorialStep) => {
    setCurrentStep(step);
  }, []);

  const markComplete = useCallback(() => {
    localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
    setHasCompletedTutorial(true);
    setIsActive(false);
    setCurrentStep('welcome'); // Reset step to prevent auto-disconnect effect
  }, []);

  return (
    <TutorialContext.Provider value={{
      isActive,
      currentStep,
      hasCompletedTutorial,
      startTutorial,
      quitTutorial,
      advanceStep,
      goToStep,
      markComplete,
    }}>
      {children}
    </TutorialContext.Provider>
  );
}
